import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Divider } from "@mui/material";

import { usePermissions } from "components/Hook";
import PeruPaySlipDetails from "components/Screens/Employee/PayslipDetails/PeruPayStubDetails/PeruPayStubDetails";
import PayslipPageLayout from "components/Screens/Employee/PayslipDetails/Layout/EmployeeDetailsLayout";
import { AlertType } from "components/common/Snackbar";

import getRequest from "util/APIHelperGet";
import { baseUrl } from "util/APIBaseUrl";
import { showSnackbar } from "store/actions/Utility";
import { numberToCurrencyFormat } from "util/utils";
import { getFormattedPayslipData } from "util/payslipDataMapper";
import { getDateInFormat, getFullMonthYearFromDate } from "helpers";
import { EmployeePaySlipDetails } from "store/actions/EmployeePortal/EmployeePaySlipDetails";

import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";
import DownloadIcon from "assets/images/download.svg";

import "./EmployeePayStubDetails.css";

function EmployeePayStubDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loggedInUser } = usePermissions();
  const { id: payrollIdFromParams } = useParams();
  const queryParams = new URLSearchParams(window.location.search);
  const payslipMonthFromParams = queryParams.get("paySlipMonth");

  const [open, setOpen] = useState(false);
  const [openMonth, setMonthOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState();
  const [selectedMonth, setSelectedMonth] = useState();
  const [payrollId, setPayrollId] = useState(payrollIdFromParams);
  const [formattedPayslipData, setFormattedPayslipData] = useState(null);

  const paySlipDetails = useSelector((state) => state.EmployeePaySlipDetails);
  const loadingPaySlipDetails = useSelector((state) => state.EmployeePaySlipDetails.isLoading);

  const { payslip } = paySlipDetails;
  const { data: payslipData, filters = [] } = payslip || {};

  const { country } = payslipData || {};
  const { user, payroll, allowance = [], deductions = [], total = {} } = formattedPayslipData || {};
  const { monthlySalary, netAmount, account, bank, month } = payroll || {};
  const { totalAllowance, totalAllowanceYTD, totalDeductions, totalDeductionsYTD } = total || {};
  const { firstName, lastName, jobTitle, employmentType, department, id: userId, joiningDate, DOB } = user || {};

  const personalInformation = [
    { label: "Name", value: `${firstName} ${lastName}` },
    { label: "Designation", value: jobTitle },
    { label: "Employee Type", value: employmentType },
    { label: "Department", value: department },
    { label: "Employee Code", value: userId },
    { label: "Date of Joining", value: getDateInFormat(joiningDate) },
    { label: "Date of Birth", value: getDateInFormat(DOB) },
  ];

  const payrollInformation = [
    { label: "Monthly Salary", value: `$${numberToCurrencyFormat(monthlySalary)}` },
    { label: "Net Amount", value: `$${numberToCurrencyFormat(netAmount)}` },
    { label: "Account #", value: `${account || "N/A"}` },
    { label: "Bank", value: `${bank || "N/A"}` },
  ];

  const headerContainerStyle = { background: "var(--color-primitives-primary-200)" };
  const personalPayrollInfoStyle = { background: "var(--color-primitives-primary-50)" };

  const shouldUseLatestPayrollApi = () => {
    const payslipMonthDate = new Date(payslipMonthFromParams);
    const may2025 = new Date("2025-05-30");
    return payslipMonthDate >= may2025;
  };

  const downloadPayslip = async () => {
    const downloadPayrollId = payslipData?.payroll?.id;
    const { userId: empUserId } = loggedInUser || {};

    const downloadUrl = shouldUseLatestPayrollApi()
      ? `${baseUrl}/employees/${empUserId}/payslips/${payrollId}?action=pdf`
      : `${baseUrl}/employee/${empUserId}/payroll/${downloadPayrollId}?action=pdf`;

    const response = await getRequest(downloadUrl, true, "export");

    if (!response?.ok) {
      dispatch(
        showSnackbar({ type: AlertType.error, message: "Payslip is not available, Please contact support ", dispatch })
      );
      return;
    }
    const url = window.URL.createObjectURL(response?.payload);
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = url;

    link.download = "Salary Slip";
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    dispatch(showSnackbar({ dispatch, type: "success", message: "Pay slip downloaded successfully!" }));
  };

  const renderTotalValues = ({ current, ytd }) => {
    return (
      <div className="flex gap-1">
        <div className="payslip-details-total">Total</div>
        <div className="payslip-details-total w-200">${numberToCurrencyFormat(current)}</div>
        <div className="payslip-details-total w-265">${numberToCurrencyFormat(ytd)}</div>
      </div>
    );
  };

  useEffect(() => {
    if (payrollId) {
      const { userId: empUserId } = loggedInUser || {};

      if (shouldUseLatestPayrollApi()) {
        dispatch(EmployeePaySlipDetails(`employees/${empUserId}/payslips/${payrollId}`));
      } else {
        dispatch(EmployeePaySlipDetails(`employee/${empUserId}/payroll/${payrollId}`));
      }
    }
  }, [payrollId]);

  useEffect(() => {
    if (!payslipData) {
      setFormattedPayslipData(null);
      return;
    }

    const formattedData = shouldUseLatestPayrollApi()
      ? getFormattedPayslipData(payslipMonthFromParams, payslipData)
      : payslipData;

    setFormattedPayslipData(formattedData);

    const { payroll: formattedPayroll, payslipMonth: formattedPayslipMonth } = formattedData || {};

    const payrollMonth = formattedPayroll?.month || formattedPayslipMonth;

    if (payrollMonth) {
      let payrollDate;

      if (shouldUseLatestPayrollApi() && payslipMonthFromParams) {
        payrollDate = new Date(payslipMonthFromParams);
      } else {
        payrollDate = new Date(payrollMonth);
      }

      setSelectedYear(payrollDate?.getUTCFullYear());
      setSelectedMonth(payrollDate?.getUTCMonth());
    }
  }, [payslipData]);

  if (country?.toLowerCase() === "peru") {
    return (
      <PayslipPageLayout
        month={payslipMonthFromParams}
        loadingPaySlipDetails={loadingPaySlipDetails}
        navigate={navigate}
        open={open}
        setOpen={setOpen}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        filters={filters}
        setPayrollId={setPayrollId}
        openMonth={openMonth}
        setMonthOpen={setMonthOpen}
      >
        <PeruPaySlipDetails downloadPayslip={downloadPayslip} payslipData={formattedPayslipData} />
      </PayslipPageLayout>
    );
  }

  return (
    <PayslipPageLayout
      month={month}
      loadingPaySlipDetails={loadingPaySlipDetails}
      navigate={navigate}
      open={open}
      setOpen={setOpen}
      selectedYear={selectedYear}
      setSelectedYear={setSelectedYear}
      selectedMonth={selectedMonth}
      setSelectedMonth={setSelectedMonth}
      filters={filters}
      setPayrollId={setPayrollId}
      openMonth={openMonth}
      setMonthOpen={setMonthOpen}
    >
      <div className="emp-payslip-container">
        <div className="emp-payslip-header-container" style={headerContainerStyle}>
          <div className="flex">
            <div className="emp-payslip-edge-logo">
              <EdgeLogo icon size="xl" />
            </div>

            <div className="align-self-center pl-1">
              <div className="emp-payslip-title">Pay slip</div>
              <div className="emp-payslip-month">{getFullMonthYearFromDate(month)}</div>
            </div>
          </div>

          <div className="emp-payslip-download-btn cursor-pointer" onClick={downloadPayslip}>
            <img src={DownloadIcon} alt="download icon" />
            Download PDF
          </div>
        </div>

        <div className="personal-payroll-info-container" style={personalPayrollInfoStyle}>
          <div>
            <div className="personal-payroll-info-title">Personal Information</div>

            {personalInformation.map((item) => (
              <div key={item.label} className="payroll-info-container">
                <div>{item.label}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>

          <div>
            <div className="personal-payroll-info-title">Payroll Information</div>
            {payrollInformation.map((item) => (
              <div className="payroll-info-container">
                <div>{item.label}</div>
                <div>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="payslip-details-heading">
          <div className="payslip-details-section1-heading">Allowance</div>
          <div className="payslip-details-section2-heading">Current Month</div>
          <div className="payslip-details-section3-heading">YTD</div>
        </div>

        {allowance?.map((item) => (
          <div className="payslip-details">
            <div className="payslip-details-data" style={{ textAlign: "left" }}>
              {item?.name}
              <Divider light />
            </div>
            <div className="payslip-details-data" style={{ width: "200px" }}>
              ${numberToCurrencyFormat(item?.amount)}
              <Divider light />
            </div>
            <div className="payslip-details-data" style={{ width: "265px" }}>
              ${numberToCurrencyFormat(item?.ytd)}
              <Divider light />
            </div>
          </div>
        ))}

        {renderTotalValues({ current: totalAllowance, ytd: totalAllowanceYTD })}

        <div className="payslip-details-heading mt-1">
          <div className="payslip-details-section1-heading">Deductions</div>
          <div className="payslip-details-section2-heading">Current Month</div>
          <div className="payslip-details-section3-heading">YTD</div>
        </div>
        {deductions?.map((deduction) => (
          <div className="payslip-details">
            <div className="payslip-details-data" style={{ textAlign: "left" }}>
              {deduction?.name}
              <Divider light />
            </div>
            <div className="payslip-details-data" style={{ width: "200px" }}>
              ${numberToCurrencyFormat(deduction?.amount)}
              <Divider light />
            </div>
            <div className="payslip-details-data" style={{ width: "265px" }}>
              ${numberToCurrencyFormat(deduction?.ytd)}
              <Divider light />
            </div>
          </div>
        ))}

        {renderTotalValues({ current: totalDeductions, ytd: totalDeductionsYTD })}

        <div className="payslip-summary-container">
          <div className="payslip-summary-details" style={{ height: "44px" }}>
            <div className="payslip-details-section1-heading grid-center">Summary</div>
            <div className="payslip-details-section2-heading h-2 grid-center">Current Month</div>
          </div>

          <div className="payslip-summary-details">
            <div className="payslip-details-section2-heading text-left" style={{ textAlign: "left" }}>
              Total Allowance <Divider light />
            </div>
            <div className="payslip-details-section2-heading h-2" style={{ width: "30%" }}>
              ${numberToCurrencyFormat(totalAllowance)}
              <Divider light />
            </div>
          </div>

          <div className="payslip-summary-details">
            <div className="payslip-details-section2-heading text-left" style={{ textAlign: "left" }}>
              Total Deductions <Divider light />
            </div>
            <div className="payslip-details-section2-heading h-2" style={{ width: "30%" }}>
              ${numberToCurrencyFormat(totalDeductions)} <Divider light />
            </div>
          </div>

          <div className="payslip-summary-details" style={{ height: "44px" }}>
            <div className="payslip-details-net-amount grid-center">Net Amount</div>
            <div className="payslip-details-net-amount grid-center">${numberToCurrencyFormat(netAmount)}</div>
          </div>
        </div>
      </div>
    </PayslipPageLayout>
  );
}

export default EmployeePayStubDetails;
